import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatINR } from "@/lib/api";
import { SkeletonRow } from "@/components/Loaders";
import { EmptyState } from "@/components/EmptyState";
import { Receipt, ArrowClockwise } from "@phosphor-icons/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const PS_STYLE = {
  PAID: "bg-green-100 text-green-700", CREATED: "bg-amber-100 text-amber-700",
  FAILED: "bg-red-100 text-red-700", REFUND_PENDING: "bg-purple-100 text-purple-700",
  REFUNDED: "bg-teal-100 text-teal-700",
};

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [status, setStatus] = useState("");
  const [ptype, setPtype] = useState("");

  const load = () => {
    setData(null);
    const qs = [];
    if (status) qs.push(`status=${status}`);
    if (ptype) qs.push(`payment_type=${ptype}`);
    api.get(`/admin/payments${qs.length ? `?${qs.join("&")}` : ""}`).then((r) => setData(r.data));
    api.get("/admin/refunds").then((r) => setRefunds(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, ptype]);

  const processRefund = async (bid) => {
    try {
      const r = await api.post(`/admin/bookings/${bid}/refund`);
      toast.success(r.data.gateway_configured === false ? "Refund marked complete (gateway not configured)." : "Refund initiated at gateway.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    }
  };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Payments</h1>
        {data && <p className="rounded-full bg-green-50 px-4 py-1.5 text-sm font-semibold text-green-700">Collected: {formatINR(data.total_collected)}</p>}
      </div>

      <Tabs defaultValue="payments" className="mt-4">
        <TabsList className="rounded-full bg-stone-100">
          <TabsTrigger value="payments" data-testid="ap-tab-payments" className="rounded-full">Transactions</TabsTrigger>
          <TabsTrigger value="refunds" data-testid="ap-tab-refunds" className="rounded-full">Refunds ({refunds.filter((r) => r.status === "refund_pending").length})</TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="mt-4">
          <div className="mb-3 flex flex-wrap gap-2">
            <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="ap-status" className="w-44 rounded-full"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {["CREATED", "PAID", "FAILED", "REFUND_PENDING", "REFUNDED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={ptype || "all"} onValueChange={(v) => setPtype(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="ap-type" className="w-40 rounded-full"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {["ADVANCE", "BALANCE", "REFUND"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
            {data === null ? (
              <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <SkeletonRow key={i} />)}</div>
            ) : data.payments.length === 0 ? (
              <EmptyState icon={Receipt} title="No payments yet" message="Payment transactions will appear here once customers pay booking advances." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking</TableHead><TableHead>Customer</TableHead><TableHead>Property</TableHead>
                    <TableHead>Payment ID</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.payments.map((p) => (
                    <TableRow key={p.id} data-testid={`ap-row-${p.id}`}>
                      <TableCell className="font-mono text-xs">{p.booking_ref}</TableCell>
                      <TableCell>{p.customer_name || "—"}</TableCell>
                      <TableCell>{p.property_name || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{p.razorpay_payment_id || p.razorpay_order_id || "—"}</TableCell>
                      <TableCell className="text-xs">{p.payment_type}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatINR(p.amount)}</TableCell>
                      <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PS_STYLE[p.payment_status] || "bg-stone-100"}`}>{p.payment_status}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="refunds" className="mt-4">
          <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
            {refunds.length === 0 ? (
              <EmptyState icon={ArrowClockwise} title="No refunds" message="Refund requests from cancellations will appear here." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking</TableHead><TableHead>Customer</TableHead><TableHead>Property</TableHead>
                    <TableHead className="text-right">Refund</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {refunds.map((b) => (
                    <TableRow key={b.id} data-testid={`ap-refund-${b.id}`}>
                      <TableCell className="font-mono text-xs">{b.booking_ref}</TableCell>
                      <TableCell>{b.customer_name}</TableCell>
                      <TableCell>{b.property_name}</TableCell>
                      <TableCell className="text-right tabular-nums text-primary">{formatINR(b.refund_amount)} ({b.refund_pct}%)</TableCell>
                      <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${b.status === "refunded" ? "bg-teal-100 text-teal-700" : "bg-purple-100 text-purple-700"}`}>{b.status === "refunded" ? "Refunded" : "Pending"}</span></TableCell>
                      <TableCell className="text-right">
                        {b.status === "refund_pending" && (
                          <Button data-testid={`process-refund-${b.id}`} size="sm" className="rounded-full bg-primary hover:bg-green-900" onClick={() => processRefund(b.id)}>Process Refund</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
